import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import CommandPalette from "./CommandPalette";
import { useGlobalCommandPaletteMounted } from "../components/ui/globalCommandPalette.js";
import LiveComponents from "./LiveComponents";
import "./styleguide-v2.scss";

// Resolve a --v2-* token to its live computed px, so size labels can't drift
// from the actual token values. Reads once after mount from the .sg-v2 root.
const useTokenPx = (rootRef, names) => {
  const [sizes, setSizes] = useState({});
  useEffect(() => {
    if (!rootRef.current) return;
    const cs = getComputedStyle(rootRef.current);
    const out = {};
    names.forEach((n) => {
      const v = cs.getPropertyValue(n).trim();
      if (v) out[n] = v;
    });
    setSizes(out);
  }, [rootRef, names]);
  return sizes;
};

// Copy a token name to the clipboard, with a brief "copied" flash on the caller.
const useCopy = () => {
  const [copied, setCopied] = useState(null);
  const copy = (text) => {
    try {
      navigator.clipboard?.writeText(text);
      setCopied(text);
      window.clearTimeout(useCopy._t);
      useCopy._t = window.setTimeout(() => setCopied(null), 1200);
    } catch (_) { /* clipboard blocked — ignore */ }
  };
  return { copied, copy };
};

/**
 * /styleguide-v2 — PROPOSED software design system.
 *
 * Adapts the marketing-website design language (retailcircle_website's
 * tokens.css + DesignSystem_StyleGuide) to the software, fixing the current
 * app's dark-on-dark surfaces, tiny fonts, and weak contrast.
 *
 * Everything is scoped under `.sg-v2` (see styleguide-v2.scss) so the rest of
 * the app is untouched until this is approved for global rollout.
 */

// ── WCAG contrast, computed live so the matrix is always true ───────────────
const hexToRgb = (h) => {
  const s = h.replace("#", "");
  return [0, 2, 4].map((i) => parseInt(s.slice(i, i + 2), 16));
};
const lum = (h) => {
  const c = hexToRgb(h).map((v) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};
const contrast = (a, b) => {
  const la = lum(a);
  const lb = lum(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};
const rate = (r) => (r >= 7 ? "AAA" : r >= 4.5 ? "AA" : r >= 3 ? "AA large" : "FAIL");
const ratingClass = (r) =>
  r === "FAIL" ? "fail" : r === "AA large" ? "warn" : "pass";

// ── Token data (mirrors styleguide-v2.scss) ─────────────────────────────────
const NEUTRALS = [
  { name: "--v2-bg", hex: "#1c1c1f", note: "Page background" },
  { name: "--v2-surface", hex: "#333336", note: "Raised card / panel" },
  { name: "--v2-surface-raised", hex: "#3d3d42", note: "Higher card / modal" },
  { name: "--v2-surface-2", hex: "#484853", note: "Highest surface" },
  { name: "--v2-text", hex: "#ffffff", note: "Primary text" },
  { name: "--v2-text-muted", hex: "#c3c3d0", note: "Secondary text" },
  { name: "--v2-text-subtle", hex: "#8f8fa5", note: "Placeholder / caption" },
  { name: "--v2-border", hex: "#484853", note: "Default hairline" },
];
const BRAND = [
  { name: "--v2-primary", hex: "#e4e4e7", note: "Primary CTA fill (graphite)" },
  // The brand's quiet grey — the counterpart to the primary fill. Everything
  // that is a control but not a call to action sits on it: the secondary button,
  // the listing card's info band and foot, the search field, the active nav row.
  // Only the light value is listed: on dark it resolves to #484853, which the
  // Neutrals row above already names as --v2-surface-2 and --v2-border.
  {
    name: "--surface-control",
    css: "var(--surface-control)",
    hex: "#ececee",
    note: "Control grey (light) — secondary button, listing card bands, search field, active nav row",
  },
  { name: "--v2-purple", hex: "#6d5cf0", note: "Accent (indigo) - focus / active / status" },
  { name: "--v2-purple-deep", hex: "#5b4be0", note: "Accent hover / pressed" },
  { name: "--v2-lilac", hex: "#a89cf6", note: "Accent text / link (on dark)" },
];
const SYSTEM = [
  { name: "--v2-danger", hex: "#ff5a65", note: "Error / destructive" },
  { name: "--v2-success", hex: "#35c66b", note: "Saved / valid" },
  { name: "--v2-warning", hex: "#f5a623", note: "Caution / pending" },
];
// No hardcoded px — the size label is resolved LIVE from the token at render
// (see useTokenPx), so the sample and its label can never disagree again.
const TYPE = [
  { name: "--v2-text-display", tag: "Display", sample: "Manage everything" },
  { name: "--v2-text-h1", tag: "H1 / page title", sample: "Manage Listings" },
  { name: "--v2-text-h2", tag: "H2 / section", sample: "Active inventory" },
  { name: "--v2-text-h3", tag: "H3", sample: "Price review" },
  { name: "--v2-text-h4", tag: "H4 / card heading", sample: "Rolex Datejust" },
  { name: "--v2-text-body", tag: "Body (default)", sample: "Default body — the readable baseline the app was missing." },
  { name: "--v2-text-caption", tag: "Caption / helper", sample: "Caption / helper / metadata." },
];
const ELEVATION = [
  { name: "--v2-bg", hex: "#1c1c1f", note: "Page background (base)" },
  { name: "--v2-surface", hex: "#333336", note: "Raised card / panel" },
  { name: "--v2-surface-raised", hex: "#3d3d42", note: "Higher card / modal" },
  { name: "--v2-surface-2", hex: "#484853", note: "Highest surface" },
];

const SPACING = [
  { name: "--v2-s1", px: 4 }, { name: "--v2-s2", px: 8 }, { name: "--v2-s3", px: 12 },
  { name: "--v2-s4", px: 16 }, { name: "--v2-s5", px: 24 }, { name: "--v2-s6", px: 32 },
  { name: "--v2-s7", px: 48 }, { name: "--v2-s8", px: 64 },
];
const RADII = [
  { name: "--v2-radius-sm", label: "8px · inputs / badges" },
  { name: "--v2-radius-md", label: "12px · cards / panels" },
  { name: "--v2-radius-lg", label: "20px · modals" },
  { name: "--v2-radius-full", label: "pill · toggles / tabs" },
];
// Reference the live CSS variables (not hard-coded dark-theme literals) so the
// swatches flip with the theme — the light theme re-maps these tokens.
const GRADIENTS = [
  { name: "--v2-gradient-primary", css: "var(--v2-gradient-primary)", note: "Primary CTA" },
  { name: "--v2-gradient-bar", css: "var(--v2-gradient-bar)", note: "Active tab / divider (flat accent)" },
  { name: "--v2-gradient-glass", css: "var(--v2-gradient-glass)", note: "Glassmorphism card fill" },
  { name: "--v2-gradient-graphite", css: "var(--v2-gradient-graphite)", note: "Graphite panel / hero backdrop (theme-matched)" },
];
const BREAKPOINTS = [
  { name: "--v2-bp-sm", px: 640, note: "Phone → large phone" },
  { name: "--v2-bp-md", px: 900, note: "Tablet" },
  { name: "--v2-bp-lg", px: 1200, note: "Laptop / desktop" },
  { name: "--v2-bp-xl", px: 1440, note: "Wide desktop" },
];

const CM_SURFACES = [
  { name: "--v2-bg", hex: "#1c1c1f" },
  { name: "--v2-surface", hex: "#333336" },
  { name: "--v2-surface-raised", hex: "#3d3d42" },
];
const CM_FG = [
  { name: "--v2-text", hex: "#ffffff" },
  { name: "--v2-text-muted", hex: "#c3c3d0" },
  { name: "--v2-text-subtle", hex: "#8f8fa5" },
  { name: "--v2-lilac (link)", hex: "#a89cf6" },
  { name: "--v2-purple", hex: "#6d5cf0" },
];

// Kanban request statuses — extracted verbatim from views/KanbanRequests.jsx
// (COLUMNS + COLUMN_STATUSES). Board order. `statuses` lists the raw DB values
// that map into each column. Documented here so the status set has one visible
// home. Since M39B the accent is the generated `--kanban-status-*` token, the
// same one KanbanRequests.scss reads — this specimen no longer keeps its own
// copy of the six hexes, so it cannot drift from the board.
const KANBAN_STATUSES = [
  { id: "status-new",         label: "New",           color: "var(--kanban-status-new)",         statuses: ["new", "listing_creation", "draft"] },
  { id: "status-in-progress", label: "Send Proposal", color: "var(--kanban-status-in-progress)", statuses: ["in_progress"] },
  { id: "status-negotiation", label: "Negotiation",   color: "var(--kanban-status-negotiation)", statuses: ["negotiation"] },
  { id: "status-follow-up",   label: "Follow-up",     color: "var(--kanban-status-follow-up)",   statuses: ["follow_up"] },
  { id: "status-deposited",   label: "Deposited",     color: "var(--kanban-status-deposited)",   statuses: ["deposited"] },
  { id: "status-done",        label: "Done",          color: "var(--kanban-status-done)",        statuses: ["done"] },
];

const Swatch = ({ c, onCopy, copied }) => (
  <figure className="sgv2__swatch">
    <button
      type="button"
      className="sgv2__chip sgv2__chip--copy"
      style={{ background: c.css || c.hex }}
      onClick={() => onCopy?.(c.name)}
      title={`Copy ${c.name}`}
      aria-label={`Copy token ${c.name}`}
    >
      <span className="sgv2__copyflag">{copied === c.name ? "Copied ✓" : "Copy"}</span>
    </button>
    <figcaption>
      <code>{c.name}</code>
      <span className="sgv2__hex">{c.hex}</span>
      {c.note && <span className="sgv2__note">{c.note}</span>}
    </figcaption>
  </figure>
);

/**
 * Spec — wraps a live specimen with its CATALOGUE NAME. This is the shared
 * vocabulary: every specimen on the page has a stable `name` (kebab-case) that
 * Guillaume can point at ("build this page with `card-raised` + `button-primary`")
 * and it maps to exactly this visual. The name is:
 *   • shown as a small chip under the specimen,
 *   • set as `data-spec="name"` on the wrapper (greppable / inspectable),
 *   • click-to-copy so the exact token is one tap away.
 */
const Spec = ({ name, children, onCopy, copied }) => (
  <div className="sgv2__spec" data-spec={name}>
    <div className="sgv2__spec-demo">{children}</div>
    {/* One consistent specimen-name style everywhere: a plain <code> chip
        (identical to the token chips). Click to copy. No bespoke pill. */}
    <code
      className="sgv2__spec-name"
      role="button"
      tabIndex={0}
      onClick={() => onCopy?.(name)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onCopy?.(name); } }}
      title={`Copy specimen name “${name}”`}
    >
      {copied === name ? "Copied ✓" : name}
    </code>
  </div>
);

// Real listings for the card specimen (live data + real Rolex imagery).
const SAMPLE_LISTINGS = [
  {
    brand: "Rolex", model: "Datejust", ref: "115234-0002", year: 2025,
    cond: "Excellent", price: "HK$9,000", status: "active", mkt: "1/3",
    image: "https://content.rolex.com/dam/2020/upright-bba/m115234-0002.png?impolicy=v6-upright",
    // Back-of-card ledger — the private deal economics kept off the front face:
    // acquisition, the payment ledger (who paid what, when), added costs, and the
    // reserve. `profit` is the sale price minus buying price and total costs.
    // Back-of-card ledger — ONLY what the front does not already say (no data
    // point appears twice across the two faces): stock status + cabinet, the
    // money we put in (purchase + costs), the linked sale, and the profit.
    back: {
      serial: "8K729104",
      status: "In stock",           // stock status of THIS item
      cabinet: "Cabinet A-3",       // where it physically sits
      buyPrice: "HK$6,200",
      costs: [
        { label: "Shipping", value: "HK$180" },
        { label: "Polishing", value: "HK$320" },
        { label: "Servicing", value: "HK$450" },
      ],
      // Linked to a sale request (not yet an order). No market price → the
      // card shows profit at list only.
      sale: { kind: "Sale request", number: "SR-1187", client: "Marcus Chen", collected: "HK$3,000", collectedOn: "12 Jul", due: "HK$6,000", dueState: "Pending" },
      market: null,
      reservedBy: "Set Lau",
    },
  },
  {
    brand: "Rolex", model: "Cosmograph Daytona", ref: "116500LN-0002", year: 2026,
    cond: "New", price: "HK$34,500", status: "active", mkt: "2/3",
    image: "https://content.rolex.com/dam/2020/upright-bba/m116500ln-0002.png?impolicy=v6-upright",
    back: {
      serial: "R4T118902",
      status: "Reserved",
      cabinet: "Safe B-1",
      buyPrice: "HK$29,800",
      costs: [
        { label: "Shipping", value: "HK$220" },
        { label: "Insurance", value: "HK$400" },
      ],
      // Linked to a confirmed order; PriceIQ has a market value → potential profit.
      sale: { kind: "Order", number: "SO-2041", client: "Amelia Wong", collected: "HK$10,000", collectedOn: "02 Aug", due: "HK$24,500", dueState: "Pending" },
      market: "HK$36,200",
      reservedBy: "Set Lau",
    },
  },
];

// Icons matching the live /rc/listings card.
const LcEdit = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M3 17.25V21h3.75L17.8 9.94l-3.75-3.75L3 17.25ZM20.7 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83Z"/></svg>
);
const LcArrow = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6"/></svg>
);
const LcChevron = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6"/></svg>
);
// Flip / rotate glyph — two curved arrows, signals "turn the card over".
const LcFlip = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M3 9a9 9 0 0 1 15-3.7L21 8M21 3v5h-5M21 15a9 9 0 0 1-15 3.7L3 16M3 21v-5h5"/></svg>
);
// The private deal ledger shown on the back of the card — the full economics of
// the deal: acquisition (serial · buy · sell), the payment ledger (who paid what
// and when), added costs (shipping / polishing / …), the reserve, and the net
// profit. Reused by the live card's flip AND the static specimen, so both stay
// in lockstep.
// Money helpers for the ledger — sample data carries formatted strings, so
// derive the sums (money in, profit, margin) from them.
const money = (s) => Number(String(s ?? "").replace(/[^0-9.]/g, "")) || 0;
const currencyOf = (s) => (String(s ?? "").match(/^[^\d]+/) || [""])[0];
const fmtMoney = (cur, n) => `${n < 0 ? "−" : ""}${cur}${Math.abs(Math.round(n)).toLocaleString("en-GB")}`;
const pct = (num, den) => (den ? `${num >= 0 ? "+" : ""}${Math.round((num / den) * 1000) / 10}%` : "");

// The private deal ledger on the back of the card. RULE: nothing the front
// already says (brand · ref · model · year · condition · list price ·
// marketplaces · active/reserved) appears here, and no data point appears
// twice on the back. Four blocks: stock (status · cabinet · serial), MONEY IN
// (purchase + costs = total), the linked SALE (number · client · collected ·
// due), and PROFIT (at market when PriceIQ has a value, else at list).
const CardBackBody = ({ l }) => {
  const b = l.back;
  const cur = currencyOf(l.price);
  const list = money(l.price);
  const costsTotal = b.costs.reduce((n, c) => n + money(c.value), 0);
  const moneyIn = money(b.buyPrice) + costsTotal;
  const profitAtList = list - moneyIn;
  const market = b.market ? money(b.market) : null;
  const profitAtMarket = market != null ? market - moneyIn : null;
  const headline = profitAtMarket ?? profitAtList;
  const collectedPct = list ? Math.min(100, Math.round((money(b.sale.collected) / list) * 100)) : 0;
  return (
    <div className="sgv2__live-back-body">
      {/* Stock line — status chip, where it sits, its serial. */}
      <div className="sgv2__live-back-head">
        <span className="sgv2__live-back-eyebrow">Deal ledger</span>
        <span className="sgv2__live-back-stock">
          <span className={`sgv2__live-back-status sgv2__live-back-status--${b.status.toLowerCase().replace(/\s+/g, "-")}`}>{b.status}</span>
          <span className="sgv2__live-back-where">{b.cabinet}<span className="sgv2__live-back-ref-sep">·</span>S/N {b.serial}</span>
        </span>
      </div>

      {/* Money in (left, emphasis) · Profit (right, emphasis). */}
      <div className="sgv2__live-back-hero">
        <div className="sgv2__live-back-kpi">
          <span className="sgv2__live-back-slabel">Money in</span>
          <span className="sgv2__live-back-kpi-val">{fmtMoney(cur, moneyIn)}</span>
        </div>
        <div className="sgv2__live-back-kpi sgv2__live-back-kpi--profit">
          <span className="sgv2__live-back-slabel">{market != null ? `Potential profit · market ${b.market}` : "Profit at list"}</span>
          <span className="sgv2__live-back-kpi-row">
            <span className={`sgv2__live-back-kpi-val sgv2__live-back-kpi-val--profit${headline < 0 ? " is-loss" : ""}`}>{fmtMoney(cur, headline)}</span>
            <span className="sgv2__live-back-margin">{pct(headline, moneyIn)}</span>
          </span>
          {market != null && <span className="sgv2__live-back-kpi-sub">at list {fmtMoney(cur, profitAtList)}</span>}
        </div>
      </div>

      {/* Money-in breakdown — purchase first, then each cost. Sums to the KPI. */}
      <ul className="sgv2__live-back-rows">
        <li className="sgv2__live-back-row"><span className="sgv2__live-back-row-label">Purchase</span><span /><span className="sgv2__live-back-row-amt">{b.buyPrice}</span></li>
        {b.costs.map((c) => (
          <li className="sgv2__live-back-row sgv2__live-back-row--cost" key={c.label}>
            <span className="sgv2__live-back-row-label">{c.label}</span><span />
            <span className="sgv2__live-back-row-amt">{c.value}</span>
          </li>
        ))}
      </ul>

      {/* Linked sale — the one place the client and the sale number appear. */}
      <div className="sgv2__live-back-sale">
        <div className="sgv2__live-back-sale-head">
          <span className="sgv2__live-back-slabel">{b.sale.kind}</span>
          <span className="sgv2__live-back-sale-no">{b.sale.number}</span>
          <span className="sgv2__live-back-sale-client">{b.sale.client}</span>
        </div>
        <div className="sgv2__live-back-collect" role="img" aria-label={`${collectedPct}% of the sale collected`}>
          <span className="sgv2__live-back-collect-track">
            <span className="sgv2__live-back-collect-fill" style={{ width: `${collectedPct}%` }} />
          </span>
          <span className="sgv2__live-back-collect-text">
            <span>Collected <b>{b.sale.collected}</b> <span className="sgv2__live-back-row-when">{b.sale.collectedOn}</span></span>
            <span>Due <b className="is-due">{b.sale.due}</b> <span className="sgv2__live-back-pill">{b.sale.dueState}</span></span>
          </span>
        </div>
      </div>

      {/* Reserved by (staff) — the client is already named on the sale line. */}
      <div className="sgv2__live-back-facts">
        <div className="sgv2__live-back-fact">
          <span className="sgv2__live-back-slabel">Reserved by</span>
          <span className="sgv2__live-back-fval sgv2__live-back-fval--client">{b.reservedBy}</span>
        </div>
      </div>
    </div>
  );
};

// The card's fixed bottom: Active/Reserve status row + the split footer (flip
// button · marketplace count). Stays put while ONLY the top flips.
const CardBottom = ({ l, active, reserved, flipped, onFlip }) => (
  <>
    <div className="sgv2__live-status sgv2__live-status--split">
      <div className="sgv2__live-trow">
        <button className={`sgv2__live-toggle sgv2__live-toggle--active${active ? " is-on" : ""}`} aria-pressed={active} aria-label="Active" />
        <span className="sgv2__live-tlabel">{active ? "ON" : "OFF"}</span>
      </div>
      <div className="sgv2__live-trow">
        <span className={`sgv2__live-tlabel${reserved ? " sgv2__live-tlabel--on" : ""}`}>RSVD</span>
        <button className={`sgv2__live-toggle sgv2__live-toggle--reserve${reserved ? " is-on" : ""}`} aria-pressed={reserved} aria-label="Reserved" />
      </div>
    </div>
    <div className="sgv2__live-footrow">
      {/* Left button toggles the TOP between the listing face and the ledger. */}
      <button
        className={`sgv2__live-mktfoot sgv2__live-mktfoot--left${flipped ? " is-on" : ""}`}
        aria-label={flipped ? "Show listing" : "Show deal ledger"}
        aria-pressed={flipped}
        onClick={onFlip}
      >
        <span className="sgv2__live-mktfoot-flip"><LcFlip /></span>
      </button>
      <button className="sgv2__live-mktfoot sgv2__live-mktfoot--right" aria-label="Marketplace" aria-expanded="false">
        <span className="sgv2__live-mktfoot-count">{l.mkt}</span>
        <span className="sgv2__live-mktfoot-chev"><LcChevron /></span>
      </button>
    </div>
  </>
);

// The card's flippable TOP: the listing face (info + photo) on the front, the
// deal ledger on the back. Only this region rotates.
const CardTop = ({ l, active }) => (
  <>
    <div className="sgv2__live-info">
      <div className="sgv2__live-meta-row">
        <span className="sgv2__live-meta">{l.brand}</span>
        <span className="sgv2__live-sep">|</span>
        <span className="sgv2__live-meta">{l.ref}</span>
      </div>
      <div className="sgv2__live-name-row">
        <span className="sgv2__live-model">{l.model}</span>
        <span className="sgv2__live-price-wrap">
          {active && <span className="sgv2__live-active-dot" aria-label="Active listing" title="Active" />}
          <span className={`sgv2__live-price${!l.price ? " sgv2__live-price--por" : ""}`}>{l.price || "Price on request"}</span>
        </span>
      </div>
      <div className="sgv2__live-meta-row">
        <span className="sgv2__live-meta sgv2__live-meta--year">{l.year}</span>
        <span className="sgv2__live-sep">|</span>
        <span className="sgv2__live-meta sgv2__live-meta--cond">{l.cond}</span>
      </div>
    </div>

    <div className="sgv2__live-photo">
      <img src={l.image} alt={`${l.brand} ${l.model}`} loading="lazy" />
      <div className="sgv2__live-icons">
        <button className="sgv2__live-icon" aria-label="Edit"><LcEdit /></button>
        <button className="sgv2__live-icon" aria-label="Open"><LcArrow /></button>
      </div>
    </div>
  </>
);

// LiveCard — the faithful /rc/listings watch card. The TOP region (info + photo)
// flips to the deal ledger when the footer flip button is pressed; the status
// row and footer buttons below stay fixed. Uses --v2-* tokens, so it's Graphite-
// coloured on the styleguide. Toggles auto-save (no button, per the RC law).
const LiveCard = ({ l, overlay }) => {
  const active = l.status === "active";
  const reserved = l.status === "reserved";
  const [flipped, setFlipped] = useState(false);
  // V1 adds --overlay: the image extends down and the toggle row is lifted onto
  // the image. --topflip scopes the 3D rotate to the top region only.
  return (
    <article className={`sgv2__live sgv2__live--v2 sgv2__live--topflip${overlay ? " sgv2__live--overlay" : ""}${flipped ? " is-flipped" : ""}`}>
      <div className="sgv2__live-flip">
        <div className="sgv2__live-face sgv2__live-face--front">
          <CardTop l={l} active={active} />
        </div>
        {/* Back of the TOP only — the deal ledger, pre-rotated to face us. */}
        <div className="sgv2__live-face sgv2__live-face--back">
          <CardBackBody l={l} />
        </div>
      </div>
      <CardBottom l={l} active={active} reserved={reserved} flipped={flipped} onFlip={() => setFlipped((f) => !f)} />
    </article>
  );
};

const TYPE_TOKENS = TYPE.map((t) => t.name);

// Working date picker — the calendar icon opens THREE rolling steppers: day ·
// month · year, left to right (DD-MM-YYYY). Roll each with ▲/▼ (or scroll / arrow
// keys). Day count clamps to the month's length. Self-contained & interactive.
const pad2 = (n) => String(n).padStart(2, "0");
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const daysIn = (m, y) => new Date(y, m + 1, 0).getDate();

// A single vertical roller: dim prev/next values above & below the current one,
// with ▲/▼ steppers. Scroll wheel and arrow keys also roll it.
const DRAG_STEP_PX = 22; // drag distance that advances one value ("crawl" speed)
const Roller = ({ label, display, onStep, ariaValue }) => {
  // Drag-to-roll ("crawling"): press and drag up/down to spin. Works for mouse
  // and touch via pointer events; accumulates movement and steps every ~22px.
  const drag = useRef(null);
  const onPointerDown = (e) => {
    drag.current = { y: e.clientY, acc: 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    d.acc += e.clientY - d.y;      // down = positive
    d.y = e.clientY;
    while (d.acc <= -DRAG_STEP_PX) { onStep(1);  d.acc += DRAG_STEP_PX; } // drag UP → increment
    while (d.acc >=  DRAG_STEP_PX) { onStep(-1); d.acc -= DRAG_STEP_PX; } // drag DOWN → decrement
  };
  const endDrag = (e) => {
    if (drag.current) { e.currentTarget.releasePointerCapture?.(e.pointerId); drag.current = null; }
  };
  return (
    <div className="sgv2__roller" role="group" aria-label={label}>
      <span className="sgv2__roller-label">{label}</span>
      <button type="button" className="sgv2__roller-btn" aria-label={`${label} up`} onClick={() => onStep(1)}>▲</button>
      <div
        className="sgv2__roller-window"
        tabIndex={0}
        role="spinbutton"
        aria-label={label}
        aria-valuetext={ariaValue}
        onWheel={(e) => { onStep(e.deltaY < 0 ? 1 : -1); }}
        onKeyDown={(e) => { if (e.key === "ArrowUp") { e.preventDefault(); onStep(1); } if (e.key === "ArrowDown") { e.preventDefault(); onStep(-1); } }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
      >
        <span className="sgv2__roller-ghost">{display.prev}</span>
        <span className="sgv2__roller-current">{display.cur}</span>
        <span className="sgv2__roller-ghost">{display.next}</span>
      </div>
      <button type="button" className="sgv2__roller-btn" aria-label={`${label} down`} onClick={() => onStep(-1)}>▼</button>
    </div>
  );
};

const DatePickerDemo = () => {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState(6);
  const [month, setMonth] = useState(7);   // 0-based (Aug)
  const [year, setYear] = useState(2026);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const dim = daysIn(month, year);
  const value = `${pad2(day)}-${pad2(month + 1)}-${year}`;

  // Wrapping steppers; day clamps to the current month length.
  const stepDay = (d) => setDay((v) => ((v - 1 + d + dim) % dim) + 1);
  const stepMonth = (d) => setMonth((v) => {
    const nm = (v + d + 12) % 12;
    setDay((cur) => Math.min(cur, daysIn(nm, year)));
    return nm;
  });
  const stepYear = (d) => setYear((v) => {
    const ny = v + d;
    setDay((cur) => Math.min(cur, daysIn(month, ny)));
    return ny;
  });

  const dayDisp  = { prev: pad2(((day - 2 + dim) % dim) + 1), cur: pad2(day), next: pad2((day % dim) + 1) };
  const monDisp  = { prev: MONTHS_SHORT[(month + 11) % 12], cur: MONTHS_SHORT[month], next: MONTHS_SHORT[(month + 1) % 12] };
  const yearDisp = { prev: year - 1, cur: year, next: year + 1 };

  return (
    <span className="sgv2__datepick" ref={ref}>
      <span className={`sgv2__stage-money sgv2__datepick-field${open ? " is-open" : ""}`}>
        <input className="sgv2__search-field" value={value} placeholder="DD-MM-YYYY" readOnly />
        <button
          type="button"
          className="sgv2__datepick-btn"
          aria-label="Open date rollers"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M3 9h18M8 2v4M16 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
        </button>
      </span>
      {open && (
        <div className="sgv2__rollers" role="dialog" aria-label="Choose date">
          {/* Day · Month · Year — left to right (DD-MM-YYYY). */}
          <Roller label="Day"   display={dayDisp}  onStep={stepDay}   ariaValue={pad2(day)} />
          <Roller label="Month" display={monDisp}  onStep={stepMonth} ariaValue={MONTHS_SHORT[month]} />
          <Roller label="Year"  display={yearDisp} onStep={stepYear}  ariaValue={String(year)} />
          <button type="button" className="sgv2__btn sgv2__btn--primary sgv2__rollers-done" onClick={() => setOpen(false)}>Done</button>
        </div>
      )}
    </span>
  );
};

const StyleGuideV2Page = () => {
  const [theme, setTheme] = useState("dark"); // Graphite: "dark" | "light"
  // Standalone this page owns ⌘K. Rendered inside the authenticated AppShell it does not: the shell
  // mounts the production palette, so the demo below keeps its trigger and drops the shortcut.
  const shellOwnsCommandShortcut = useGlobalCommandPaletteMounted();
  const [toast, setToast] = useState(null);
  // The guide's theme toggle also drives the app-level tokens (--surface-*, --text-*…) that the
  // live components read, so real components never render light inside a dark guide (or vice versa).
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute("data-theme");
    root.setAttribute("data-theme", theme);
    return () => (previous ? root.setAttribute("data-theme", previous) : root.removeAttribute("data-theme"));
  }, [theme]);
  const rootRef = useRef(null);
  const typePx = useTokenPx(rootRef, TYPE_TOKENS);
  const { copied, copy } = useCopy();

  // Stop the annoying auto-scroll to the middle on reload: if the URL carries a
  // #section hash, the browser jumps to it on load. Strip the hash and pin to the
  // top on mount. TOC links still scroll on click (that's a user action, not load).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    window.scrollTo(0, 0);
  }, []);

  // A command "runs": show a confirmation toast.
  const runCommand = (a) => {
    setToast(a.label);
    window.clearTimeout(runCommand._t);
    runCommand._t = window.setTimeout(() => setToast(null), 2600);
  };
  const matrix = CM_FG.map((fg) => ({
    fg,
    cells: CM_SURFACES.map((bg) => {
      const r = contrast(fg.hex, bg.hex);
      return { bg, ratio: r.toFixed(1), rating: rate(r) };
    }),
  }));

  return (
    <div className={`sg-v2${theme === "light" ? " sg-v2--light" : ""}`} ref={rootRef}>
      <div className="sgv2__wrap">
        <header>
          <div className="sgv2__themebar">
            <span className="sgv2__themebar-label">Graphite</span>
            <div className="sgv2__themeswitch" role="group" aria-label="Theme">
              <button
                type="button"
                className={`sgv2__themeswitch-btn${theme === "dark" ? " is-active" : ""}`}
                aria-pressed={theme === "dark"}
                onClick={() => setTheme("dark")}
              >Dark</button>
              <button
                type="button"
                className={`sgv2__themeswitch-btn${theme === "light" ? " is-active" : ""}`}
                aria-pressed={theme === "light"}
                onClick={() => setTheme("light")}
              >Light</button>
            </div>
          </div>
          <p className="sgv2__eyebrow">Retail Circle · Software</p>
          <h1 className="sgv2__title">Design System v2</h1>
          <p className="sgv2__lead">
            A proposed system for the software, built from the marketing site's
            visual language. It fixes what's cramped and dim in the current app:
            lighter surfaces for real depth, a 16px reading baseline, and colour
            pairings that are proven legible.
          </p>

          <nav className="sgv2__toc" aria-label="Sections">
            {/* Order matches the banded page layout (see the reorder block in
                styleguide-v2.scss): Foundations → Small parts → Big → Reference. */}
            <a href="#colours">Colours</a>
            <a href="#contrast">Contrast</a>
            <a href="#type">Type</a>
            <a href="#spacing">Spacing</a>
            <a href="#radii">Radii</a>
            <a href="#elevation">Elevation</a>
            <a href="#gradients">Gradients</a>
            <a href="#motion">Motion</a>
            <a href="#breakpoints">Breakpoints</a>
            <a href="#components">Components</a>
            <a href="#states">States</a>
            <a href="#live">Live</a>
            <a href="#command">⌘K palette</a>
            <a href="#feedback">Empty &amp; loading</a>
            <a href="#card">Listing card</a>
            <a href="#ba">Before / after</a>
            <a href="#guidance">Do / Don't</a>
            <a href="#staging">Staging</a>
          </nav>
        </header>

        {/* ── COLOURS ── */}
        <section className="sgv2__section" id="colours">
          <h2 className="sgv2__h2">Colours</h2>
          <h3 className="sgv2__h3">Neutrals — the elevation fix</h3>
          <p className="sgv2__meta">
            Depth on dark comes from lighter surfaces, not shadow. These are the
            values that fix dark-on-dark.
          </p>
          <div className="sgv2__swatches">{NEUTRALS.map((c) => <Swatch key={c.name} c={c} onCopy={copy} copied={copied} />)}</div>

          <h3 className="sgv2__h3">Brand</h3>
          <div className="sgv2__swatches">{BRAND.map((c) => <Swatch key={c.name} c={c} onCopy={copy} copied={copied} />)}</div>

          <h3 className="sgv2__h3">System</h3>
          <div className="sgv2__swatches">{SYSTEM.map((c) => <Swatch key={c.name} c={c} onCopy={copy} copied={copied} />)}</div>
        </section>

        {/* ── CONTRAST ── */}
        <section className="sgv2__section" id="contrast">
          <h2 className="sgv2__h2">Contrast matrix</h2>
          <p className="sgv2__meta">
            Computed live (WCAG 2.1). AA = safe for body, AA large = 18px+/bold
            only, FAIL = never for text. Purple is AA-large only — headings and
            big CTAs, never body copy.
          </p>
          <div className="sgv2__cm-wrap">
            <table className="sgv2__cm">
              <thead>
                <tr>
                  <th>text / accent</th>
                  {CM_SURFACES.map((s) => <th key={s.name}><code>{s.name}</code></th>)}
                </tr>
              </thead>
              <tbody>
                {matrix.map((row) => (
                  <tr key={row.fg.name}>
                    <th scope="row"><code>{row.fg.name}</code></th>
                    {row.cells.map((cell) => (
                      <td
                        key={cell.bg.name}
                        className={`sgv2__cm-cell sgv2__cm-cell--${ratingClass(cell.rating)}`}
                        style={{ background: cell.bg.hex, color: row.fg.hex }}
                      >
                        <span className="sgv2__cm-ratio">{cell.ratio}</span>
                        <span className="sgv2__cm-rating">{cell.rating}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* ── TYPE ── */}
        <section className="sgv2__section" id="type">
          <h2 className="sgv2__h2">Typography</h2>
          <p className="sgv2__meta">
            Neue Haas Display (Medium headings / Light body), the website face —
            now loaded in the app. 8-step scale, 16px body baseline.
          </p>
          <div className="sgv2__type">
            {TYPE.map((t) => {
              // Body / lead / caption are prose tokens — they render at regular
              // body weight in the real app, not the medium heading weight.
              const isBodyWeight = ["--v2-text-body-lg", "--v2-text-body", "--v2-text-caption"].includes(t.name);
              return (
                <div className="sgv2__type-row" key={t.name}>
                  <div className="sgv2__type-labels">
                    <code>{t.name}</code>
                    <span className="sgv2__note">{t.tag}{typePx[t.name] ? ` · ${typePx[t.name]}` : ""}</span>
                  </div>
                  <p className={`sgv2__type-sample${isBodyWeight ? " sgv2__type-sample--body" : ""}`} style={{ fontSize: `var(${t.name})` }}>{t.sample}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── SPACING ── */}
        <section className="sgv2__section" id="spacing">
          <h2 className="sgv2__h2">Spacing</h2>
          <p className="sgv2__meta">Tapered 8px scale — tight at the small end for dense stacks, wide jumps for section rhythm. Pick by role, not px.</p>
          <div className="sgv2__scale">
            {SPACING.map((s) => (
              <div className="sgv2__scale-row" key={s.name}>
                <div className="sgv2__scale-labels"><code>{s.name}</code><span className="sgv2__note">{s.px}px</span></div>
                <span className="sgv2__scale-bar" style={{ width: `var(${s.name})` }} />
              </div>
            ))}
          </div>
        </section>

        {/* ── RADII ── */}
        <section className="sgv2__section" id="radii">
          <h2 className="sgv2__h2">Radii</h2>
          <div className="sgv2__radii">
            {RADII.map((r) => (
              <figure className="sgv2__swatch" key={r.name}>
                <span className="sgv2__radius-tile" style={{ borderRadius: `var(${r.name})` }} />
                <figcaption><code>{r.name}</code><span className="sgv2__note">{r.label}</span></figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* ── ELEVATION ── */}
        <section className="sgv2__section" id="elevation">
          <h2 className="sgv2__h2">Elevation</h2>
          <p className="sgv2__meta">
            Stack surface tiers for cards and panels — each step is visibly
            lighter, so layers read without a single drop shadow.
          </p>
          <div className="sgv2__elev">
            {ELEVATION.map((s) => (
              <figure className="sgv2__swatch" key={s.name}>
                <span className="sgv2__elev-tile" style={{ background: s.hex }} />
                <figcaption>
                  <code>{s.name}</code>
                  <span className="sgv2__hex">{s.hex}</span>
                  <span className="sgv2__note">{s.note}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* ── GRADIENTS ── */}
        <section className="sgv2__section" id="gradients">
          <h2 className="sgv2__h2">Gradients</h2>
          <p className="sgv2__meta">The RC signature treatments. Software uses them sparingly — the primary CTA, an active-tab bar, a glass fill — not as page decoration.</p>
          <div className="sgv2__gradients">
            {GRADIENTS.map((g) => (
              <figure className="sgv2__swatch" key={g.name}>
                <span className="sgv2__gradient-tile" style={{ background: g.css }} />
                <figcaption><code>{g.name}</code><span className="sgv2__note">{g.note}</span></figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* ── MOTION ── */}
        <section className="sgv2__section" id="motion">
          <h2 className="sgv2__h2">Motion</h2>
          <p className="sgv2__meta">One duration (.22s), one easing. The signature is a subtle press on action, a small lift on cards. Hover the tiles. Respects reduced-motion.</p>
          <div className="sgv2__motion">
            <figure className="sgv2__swatch">
              <span className="sgv2__motion-tile sgv2__motion-tile--press" />
              <figcaption><code>--v2-press</code><span className="sgv2__note">scale(.97) — buttons</span></figcaption>
            </figure>
            <figure className="sgv2__swatch">
              <span className="sgv2__motion-tile sgv2__motion-tile--lift" />
              <figcaption><code>translateY(-4px)</code><span className="sgv2__note">lift — cards / tiles</span></figcaption>
            </figure>
            <figure className="sgv2__swatch">
              <span className="sgv2__motion-tile sgv2__motion-tile--fade" />
              <figcaption><code>--v2-transition</code><span className="sgv2__note">.22s — all state changes</span></figcaption>
            </figure>
          </div>
        </section>

        {/* ── BREAKPOINTS ── */}
        <section className="sgv2__section" id="breakpoints">
          <h2 className="sgv2__h2">Breakpoints</h2>
          <p className="sgv2__meta">The canonical stops. CSS vars can't be used in @media — treat these as the single reference every component's queries must match.</p>
          <div className="sgv2__scale">
            {BREAKPOINTS.map((b) => (
              <div className="sgv2__scale-row" key={b.name}>
                <div className="sgv2__scale-labels"><code>{b.name}</code><span className="sgv2__note">{b.px}px · {b.note}</span></div>
                <span className="sgv2__bp-bar" style={{ width: `${b.px / 14}px` }} />
              </div>
            ))}
          </div>
        </section>

        {/* ── COMPONENTS ── */}
        <section className="sgv2__section" id="components">
          <h2 className="sgv2__h2">Components</h2>

          <h3 className="sgv2__h3">Buttons</h3>
          <div className="sgv2__btn-row">
            <Spec name="button-primary" onCopy={copy} copied={copied}>
              <button type="button" className="sgv2__btn sgv2__btn--primary">Primary action</button>
            </Spec>
            <Spec name="button-secondary" onCopy={copy} copied={copied}>
              <button type="button" className="sgv2__btn sgv2__btn--secondary">Secondary</button>
            </Spec>
            <Spec name="button-ghost" onCopy={copy} copied={copied}>
              <button type="button" className="sgv2__btn sgv2__btn--ghost">Ghost</button>
            </Spec>
          </div>

          <h3 className="sgv2__h3">Cards — surface tiers</h3>
          <div className="sgv2__cards">
            <Spec name="card-solid" onCopy={copy} copied={copied}>
              <div className="sgv2__card">
                <p className="sgv2__card-h">Solid</p>
              </div>
            </Spec>
            <Spec name="card-raised" onCopy={copy} copied={copied}>
              <div className="sgv2__card sgv2__card--raised">
                <p className="sgv2__card-h">Raised</p>
              </div>
            </Spec>
            <Spec name="card-glass" onCopy={copy} copied={copied}>
              <div className="sgv2__card sgv2__card--glass">
                <p className="sgv2__card-h">Glass</p>
              </div>
            </Spec>
          </div>

          <h3 className="sgv2__h3">Badges</h3>
          <div className="sgv2__btn-row">
            <Spec name="badge-muted" onCopy={copy} copied={copied}>
              <span className="sgv2__badge sgv2__badge--muted">Draft</span>
            </Spec>
            <Spec name="badge-accent" onCopy={copy} copied={copied}>
              <span className="sgv2__badge sgv2__badge--accent">New</span>
            </Spec>
            <Spec name="badge-success" onCopy={copy} copied={copied}>
              <span className="sgv2__badge sgv2__badge--success">Active</span>
            </Spec>
            <Spec name="badge-warning" onCopy={copy} copied={copied}>
              <span className="sgv2__badge sgv2__badge--warning">Pending</span>
            </Spec>
            <Spec name="badge-danger" onCopy={copy} copied={copied}>
              <span className="sgv2__badge sgv2__badge--danger">Failed</span>
            </Spec>
          </div>

          {/* Real Kanban request statuses (from KanbanRequests.jsx), in board
              order with their live accent colours — the canonical status set. */}
          <h3 className="sgv2__h3">Kanban statuses</h3>
          <div className="sgv2__btn-row">
            {KANBAN_STATUSES.map((s) => (
              <Spec key={s.id} name={s.id} onCopy={copy} copied={copied}>
                <span
                  className="sgv2__badge"
                  style={{
                    color: s.color,
                    background: `color-mix(in srgb, ${s.color} 16%, transparent)`,
                    borderColor: `color-mix(in srgb, ${s.color} 45%, transparent)`,
                  }}
                >
                  {s.label}
                </span>
              </Spec>
            ))}
          </div>

          {/* Search bar — the canonical SearchInput (magnifier + field + clear). */}
          <h3 className="sgv2__h3">Search</h3>
          <div className="sgv2__btn-row">
            <Spec name="search-default" onCopy={copy} copied={copied}>
              <span className="sgv2__search">
                <svg className="sgv2__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                  <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input className="sgv2__search-field" placeholder="Quick search for products" />
              </span>
            </Spec>
            <Spec name="search-filled" onCopy={copy} copied={copied}>
              <span className="sgv2__search">
                <svg className="sgv2__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                  <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input className="sgv2__search-field" defaultValue="Rolex Datejust" />
                <button className="sgv2__search-clear" aria-label="Clear">×</button>
              </span>
            </Spec>
          </div>
        </section>

        {/* ── STATES ── */}
        <section className="sgv2__section" id="states">
          <h2 className="sgv2__h2">Component states</h2>
          <p className="sgv2__meta">
            The Airbnb lesson: an element is only finished when every state is
            designed. Each primitive below is shown across its full matrix —
            default, hover, focus, active, disabled, and (where it applies) error
            and loading. One shared focus ring, one transition, everywhere.
          </p>
          <p className="sgv2__meta">
            Every specimen carries its <strong>name</strong> in the one shared
            chip style (<code>component-state</code>) and a matching{" "}
            <code>data-spec</code> id — so you can point at an exact, collision-free
            name (e.g. <code>toggle-on</code>, <code>tab-active</code>) and it maps
            to this exact visual. See{" "}
            <code>src/styleguide/NAMING-CONVENTION.md</code>.
          </p>

          <div className="sgv2__states">
            {/* Button */}
            <div>
              <h3 className="sgv2__h3">
                Button — primary
                <span className="sgv2__name" title="Design-system name · visual tier">
                  <code>button</code> · <em>cta-primary</em>
                </span>
              </h3>
              <div className="sgv2__state-grid">
                <div className="sgv2__state-cell" data-spec="button-default">
                  <button className="sgv2__btn sgv2__btn--primary">Default</button>
                  <code className="sgv2__spec-name">button-default</code>
                </div>
                <div className="sgv2__state-cell" data-spec="button-hover">
                  <button className="sgv2__btn sgv2__btn--primary sgv2__btn--is-hover">Hover</button>
                  <code className="sgv2__spec-name">button-hover</code>
                </div>
                <div className="sgv2__state-cell" data-spec="button-focus">
                  <button className="sgv2__btn sgv2__btn--primary sgv2__btn--is-focus">Focus</button>
                  <code className="sgv2__spec-name">button-focus</code>
                </div>
                <div className="sgv2__state-cell" data-spec="button-active">
                  <button className="sgv2__btn sgv2__btn--primary sgv2__btn--is-active">Active</button>
                  <code className="sgv2__spec-name">button-active</code>
                </div>
                <div className="sgv2__state-cell" data-spec="button-loading">
                  <button className="sgv2__btn sgv2__btn--primary sgv2__btn--is-loading">Loading</button>
                  <code className="sgv2__spec-name">button-loading</code>
                </div>
                <div className="sgv2__state-cell" data-spec="button-disabled">
                  <button className="sgv2__btn sgv2__btn--primary" disabled>Disabled</button>
                  <code className="sgv2__spec-name">button-disabled</code>
                </div>
              </div>
            </div>

            {/* Input */}
            <div>
              <h3 className="sgv2__h3">
                Input
                <span className="sgv2__name" title="Design-system name · visual tier">
                  <code>input</code> · <em>field-text</em>
                </span>
              </h3>
              <div className="sgv2__state-grid">
                <div className="sgv2__field" data-spec="input-default">
                  <input className="sgv2__input" placeholder="Reference number" />
                  <code className="sgv2__spec-name">input-default</code>
                </div>
                <div className="sgv2__field" data-spec="input-focus">
                  <input className="sgv2__input sgv2__input--is-focus" defaultValue="126334" />
                  <code className="sgv2__spec-name">input-focus</code>
                </div>
                <div className="sgv2__field" data-spec="input-error">
                  <input className="sgv2__input sgv2__input--is-error" defaultValue="not a ref" />
                  <span className="sgv2__field-help sgv2__field-help--error">Enter a valid reference number.</span>
                  <code className="sgv2__spec-name">input-error</code>
                </div>
                <div className="sgv2__field" data-spec="input-disabled">
                  <input className="sgv2__input" defaultValue="RC-000" disabled />
                  <code className="sgv2__spec-name">input-disabled</code>
                </div>
              </div>
            </div>

            {/* Toggle */}
            <div>
              <h3 className="sgv2__h3">
                Toggle
                <span className="sgv2__name" title="Design-system name · visual tier">
                  <code>toggle</code> · <em>switch-pill</em>
                </span>
              </h3>
              <div className="sgv2__state-grid">
                <div className="sgv2__state-cell" data-spec="toggle-off">
                  <span className="sgv2__switch-row"><button className="sgv2__switch" aria-pressed="false" /> Off</span>
                  <code className="sgv2__spec-name">toggle-off</code>
                </div>
                <div className="sgv2__state-cell" data-spec="toggle-on">
                  <span className="sgv2__switch-row"><button className="sgv2__switch sgv2__switch--on" aria-pressed="true" /> On</span>
                  <code className="sgv2__spec-name">toggle-on</code>
                </div>
                <div className="sgv2__state-cell" data-spec="toggle-focus">
                  <span className="sgv2__switch-row"><button className="sgv2__switch sgv2__switch--on sgv2__switch--is-focus" aria-pressed="true" /> Focus</span>
                  <code className="sgv2__spec-name">toggle-focus</code>
                </div>
                <div className="sgv2__state-cell" data-spec="toggle-disabled">
                  <span className="sgv2__switch-row"><button className="sgv2__switch sgv2__switch--is-disabled" disabled /> Disabled</span>
                  <code className="sgv2__spec-name">toggle-disabled</code>
                </div>
                {/* Semantic on-state tones from the live listing card: green
                    Active and amber Reserved. */}
                <div className="sgv2__state-cell" data-spec="toggle-active">
                  <span className="sgv2__switch-row"><button className="sgv2__switch sgv2__switch--on sgv2__switch--active" aria-pressed="true" /> Active</span>
                  <code className="sgv2__spec-name">toggle-active</code>
                </div>
                <div className="sgv2__state-cell" data-spec="toggle-reserved">
                  <span className="sgv2__switch-row"><button className="sgv2__switch sgv2__switch--on sgv2__switch--reserved" aria-pressed="true" /> Reserved</span>
                  <code className="sgv2__spec-name">toggle-reserved</code>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div>
              <h3 className="sgv2__h3">
                Tab pill
                <span className="sgv2__name" title="Design-system name · visual tier">
                  <code>tab</code> · <em>pill-tab</em>
                </span>
              </h3>
              <div className="sgv2__state-grid">
                <div className="sgv2__state-cell" data-spec="tab-default">
                  <button className="sgv2__tab">Listings</button>
                  <code className="sgv2__spec-name">tab-default</code>
                </div>
                <div className="sgv2__state-cell" data-spec="tab-hover">
                  <button className="sgv2__tab sgv2__tab--is-hover">Listings</button>
                  <code className="sgv2__spec-name">tab-hover</code>
                </div>
                <div className="sgv2__state-cell" data-spec="tab-active">
                  <button className="sgv2__tab sgv2__tab--is-active">Listings</button>
                  <code className="sgv2__spec-name">tab-active</code>
                </div>
              </div>
            </div>

            {/* Checkbox */}
            <div>
              <h3 className="sgv2__h3">
                Checkbox
                <span className="sgv2__name" title="Design-system name · visual tier">
                  <code>checkbox</code> · <em>check-box</em>
                </span>
              </h3>
              <div className="sgv2__state-grid">
                <div className="sgv2__state-cell" data-spec="checkbox-off">
                  <span className="sgv2__check-row"><span className="sgv2__check" /> Unchecked</span>
                  <code className="sgv2__spec-name">checkbox-off</code>
                </div>
                <div className="sgv2__state-cell" data-spec="checkbox-on">
                  <span className="sgv2__check-row"><span className="sgv2__check sgv2__check--on">✓</span> Checked</span>
                  <code className="sgv2__spec-name">checkbox-on</code>
                </div>
                <div className="sgv2__state-cell" data-spec="checkbox-focus">
                  <span className="sgv2__check-row"><span className="sgv2__check sgv2__check--on sgv2__check--is-focus">✓</span> Focus</span>
                  <code className="sgv2__spec-name">checkbox-focus</code>
                </div>
                <div className="sgv2__state-cell" data-spec="checkbox-disabled">
                  <span className="sgv2__check-row"><span className="sgv2__check sgv2__check--is-disabled" /> Disabled</span>
                  <code className="sgv2__spec-name">checkbox-disabled</code>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── ⌘K COMMAND PALETTE ── */}
        {/* ── LIVE COMPONENTS (registry-driven) ── Fontaine M07: the REAL components/ui
            primitives rendered through the ComponentEntry mould. The mock-ups in
            Components / States above are the TARGET; each disappears when its
            primitive reaches parity in Phase 1. */}
        <section className="sgv2__section" id="live">
          <h2 className="sgv2__h2">Live components</h2>
          <p className="sgv2__lede">
            What the app renders <strong>today</strong> through <code>components/ui</code> — imported, not
            drawn. The sections above are the target; this lane is the truth. A primitive is done when
            its live entry matches its target and the target mock-up can be deleted. The coverage panel
            turns red for any export of <code>components/ui</code> without a registry entry.
          </p>
          <LiveComponents />
        </section>

        <section className="sgv2__section" id="command">
          <h2 className="sgv2__h2">Command palette</h2>
          <p className="sgv2__meta">
            The keyboard-first signature of Linear, Notion, and Stripe — one place
            to search and run anything. This is a real, working demo: press{" "}
            <strong>⌘K</strong> (or Ctrl+K) anywhere on this page, type to filter,
            ↑/↓ to move, Enter to run, Esc to close. Spring enter/exit, focus
            trapped and restored, reduced-motion respected.
          </p>
          <CommandPalette onRun={runCommand} globalShortcut={!shellOwnsCommandShortcut} />
        </section>

        {/* ── EMPTY & LOADING ── */}
        <section className="sgv2__section" id="feedback">
          <h2 className="sgv2__h2">Empty &amp; loading</h2>
          <p className="sgv2__meta">
            The Airbnb lesson: never a blank screen. An empty state invites the
            next action; a skeleton shows the shape of what's coming instead of a
            spinner on nothing. Both are first-class, not afterthoughts.
          </p>

          <h3 className="sgv2__h3">Empty states</h3>
          <div className="sgv2__cards sgv2__cards--empties">
            <Spec name="empty-first-run" onCopy={copy} copied={copied}>
              <div className="sgv2__empty">
                <span className="sgv2__empty-icon" aria-hidden="true">+</span>
                <p className="sgv2__empty-title">No listings yet</p>
                <p className="sgv2__empty-body">Add your first watch and it'll show up here, ready to price and publish.</p>
                <div className="sgv2__empty-actions">
                  <button className="sgv2__btn sgv2__btn--primary">Add a listing</button>
                </div>
              </div>
            </Spec>
            <Spec name="empty-no-results" onCopy={copy} copied={copied}>
              <div className="sgv2__empty">
                <span className="sgv2__empty-icon" aria-hidden="true">⌕</span>
                <p className="sgv2__empty-title">No matches for “Daytona”</p>
                <p className="sgv2__empty-body">Try a broader search, or clear the filters to see everything.</p>
                <div className="sgv2__empty-actions">
                  <button className="sgv2__btn sgv2__btn--secondary">Clear filters</button>
                </div>
              </div>
            </Spec>
            <Spec name="empty-error" onCopy={copy} copied={copied}>
              <div className="sgv2__empty sgv2__empty--error">
                <span className="sgv2__empty-icon" aria-hidden="true">!</span>
                <p className="sgv2__empty-title">Couldn't load listings</p>
                <p className="sgv2__empty-body">The request timed out. Your work is safe — try again.</p>
                <div className="sgv2__empty-actions">
                  <button className="sgv2__btn sgv2__btn--primary">Retry</button>
                </div>
              </div>
            </Spec>
          </div>

          <h3 className="sgv2__h3">Skeleton loaders</h3>
          <p className="sgv2__meta">Rendered while data is in flight — the same layout as the real content, so nothing jumps when it arrives.</p>
          <Spec name="skeleton-card" onCopy={copy} copied={copied}>
            <div className="sgv2__skel-cards">
              {/* One card is enough to show the shape — the loader is the same
                  layout repeated, so a single specimen communicates it. */}
              <div className="sgv2__skel-card">
                <div className="sgv2__skeleton sgv2__skel-block" />
                <div className="sgv2__skel-lines">
                  <div className="sgv2__skeleton sgv2__skel-line sgv2__skel-line--md" />
                  <div className="sgv2__skeleton sgv2__skel-line sgv2__skel-line--lg" />
                  <div className="sgv2__skeleton sgv2__skel-line sgv2__skel-line--sm" />
                </div>
              </div>
            </div>
          </Spec>
        </section>

        {/* ── LISTING CARD — big component, at the end of the parts ── */}
        <section className="sgv2__section" id="card">
          <h2 className="sgv2__h2">Listing card</h2>
          <p className="sgv2__meta">
            The live <code>/rc/listings</code> watch card — info header, title +
            price, year | condition, the photo with edit &amp; open icons, and the
            status bar with the Marketplace dropdown and Active / Reserve toggles —
            in the Graphite colour system. Toggles auto-save; there is no Save button.
            Each version shows the <strong>front on the left</strong> and its{" "}
            <strong>back — the deal ledger — on the right</strong>. On the live
            card, the flip button turns only the top over to reveal the ledger;
            the status row and buttons below stay put.
          </p>
          <div className="sgv2__live-grid">
            {SAMPLE_LISTINGS.map((l, i) => (
              <Spec key={l.ref} name={i === 0 ? "listing-card" : "listing-card-v2"} onCopy={copy} copied={copied}>
                {/* Front (left, live & flippable) + back (right, static). V1
                    (i===0) gets the --overlay variant: toggles sit on the image. */}
                <div className="sgv2__live-pair">
                  <div className="sgv2__live-pair-col">
                    <span className="sgv2__live-pair-tag">Front</span>
                    <LiveCard l={l} overlay={i === 0} />
                  </div>
                  <div className="sgv2__live-pair-col">
                    <span className="sgv2__live-pair-tag">Back — deal ledger</span>
                    {/* Match the front's variant so the back card is the EXACT
                        same size (V1 --overlay is taller than V2). */}
                    <article className={`sgv2__live sgv2__live--v2 sgv2__live--backonly${i === 0 ? " sgv2__live--overlay" : ""}`}>
                      <CardBackBody l={l} />
                    </article>
                  </div>
                </div>
              </Spec>
            ))}
          </div>
        </section>

        {/* ── DO / DON'T ── */}
        <section className="sgv2__section" id="guidance">
          <h2 className="sgv2__h2">Using the system</h2>
          <p className="sgv2__meta">The rules that keep the app consistent as it grows.</p>

          {/* Non-negotiable laws — must hold in every screen we build. */}
          <div className="sgv2__laws">
            <p className="sgv2__laws-head">Non-negotiable</p>
            <ul>
              <li>
                <strong>Auto-save everything, always. No Save buttons — anywhere.</strong>{" "}
                Every edit persists the moment it's made (debounced). The user
                never confirms a save; a quiet status shows <em>Saving… → Saved ✓</em>,
                and a failure surfaces inline with an automatic retry. If you're
                reaching for a "Save" button, stop — wire auto-save instead.
              </li>
              <li>Pick tokens by role; body text is 16px; depth from lighter surfaces, not shadow.</li>
            </ul>
          </div>

          <pre className="sgv2__snippet"><code>{`// role, not number
color: var(--v2-text-muted);   background: var(--v2-surface);
font-size: var(--v2-text-body);  // 16px baseline`}</code></pre>
          <div className="sgv2__dodont">
            <div className="sgv2__do">
              <p className="sgv2__dodont-head">Do</p>
              <ul>
                <li><strong>Auto-save every edit</strong> and show a live save status.</li>
                <li>Pick tokens by <strong>role</strong> — <code>--v2-surface</code>, not "that grey".</li>
                <li>Stack surface tiers for depth; reserve shadow for truly floating layers.</li>
                <li>One primary action per view — everything else secondary / ghost.</li>
                <li>Check the contrast matrix before putting a colour on a surface.</li>
              </ul>
            </div>
            <div className="sgv2__dont">
              <p className="sgv2__dodont-head">Don't</p>
              <ul>
                <li><strong>Add a Save / Submit / Apply button to persist data</strong> — auto-save instead.</li>
                <li>Hard-code a hex or a px size — always a token.</li>
                <li>Put purple (or any AA-large-only colour) on body-size text.</li>
                <li>Use drop shadows for elevation on the dark theme — they don't read.</li>
                <li>Ship a control without its hover / focus / disabled states.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── BEFORE / AFTER (reference, moved to the end) ── */}
        <section className="sgv2__section" id="ba">
          <h2 className="sgv2__h2">Before / after</h2>
          <p className="sgv2__meta">
            The same card, today vs. proposed. Left mimics the current app —
            #1a1a1a on #141414, 13px, #797979 metadata. Right uses v2.
          </p>
          <div className="sgv2__ba">
            <div className="sgv2__ba-panel sgv2__ba-panel--before">
              <span className="sgv2__ba-tag">Now</span>
              <p className="sgv2__ba-h--before">Rolex Datejust</p>
              <p className="sgv2__ba-p--before">Ref 126334 · 2024 · Excellent · HK$9,000</p>
            </div>
            <div className="sgv2__ba-panel sgv2__ba-panel--after">
              <span className="sgv2__ba-tag">v2</span>
              <p className="sgv2__ba-h--after">Rolex Datejust</p>
              <p className="sgv2__ba-p--after">Ref 126334 · 2024 · Excellent · HK$9,000</p>
            </div>
          </div>
        </section>

        {/* ═══ STAGING — components pulled from the app, NOT yet promoted ═══
            Review colours + sizes here, then move each into its proper section
            above. Everything is scoped with sgv2__stage-* so it's easy to lift. */}
        <section className="sgv2__section sgv2__stage" id="staging">
          <h2 className="sgv2__h2">Staging — review before adding</h2>

          {/* Select / dropdown */}
          <h3 className="sgv2__h3">Select / dropdown</h3>
          <div className="sgv2__btn-row">
            <Spec name="select-default" onCopy={copy} copied={copied}>
              <button className="sgv2__stage-select" type="button">
                <span>All brands</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </Spec>
            <Spec name="select-open" onCopy={copy} copied={copied}>
              <div className="sgv2__stage-selectwrap">
                <button className="sgv2__stage-select sgv2__stage-select--open" type="button">
                  <span>Rolex</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <ul className="sgv2__stage-menu">
                  <li className="sgv2__stage-menu-item sgv2__stage-menu-item--active">Rolex</li>
                  <li className="sgv2__stage-menu-item">Patek Philippe</li>
                  <li className="sgv2__stage-menu-item">Audemars Piguet</li>
                </ul>
              </div>
            </Spec>
          </div>

          {/* Icon button */}
          <h3 className="sgv2__h3">Icon button</h3>
          <div className="sgv2__btn-row">
            <Spec name="icon-default" onCopy={copy} copied={copied}>
              <button className="sgv2__stage-iconbtn" aria-label="Edit"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 17.25V21h3.75L17.8 9.94l-3.75-3.75L3 17.25Z" fill="currentColor"/></svg></button>
            </Spec>
            <Spec name="icon-ghost" onCopy={copy} copied={copied}>
              <button className="sgv2__stage-iconbtn sgv2__stage-iconbtn--ghost" aria-label="More"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg></button>
            </Spec>
            <Spec name="icon-back" onCopy={copy} copied={copied}>
              <button className="sgv2__stage-iconbtn" aria-label="Back"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></button>
            </Spec>
          </div>

          {/* Split button (button + dropdown) */}
          <h3 className="sgv2__h3">Split button</h3>
          <div className="sgv2__btn-row">
            <Spec name="split-button" onCopy={copy} copied={copied}>
              <span className="sgv2__stage-split">
                <button className="sgv2__btn sgv2__btn--primary sgv2__stage-split-main" type="button">Change status</button>
                <button className="sgv2__btn sgv2__btn--primary sgv2__stage-split-caret" type="button" aria-label="More options"><svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></button>
              </span>
            </Spec>
          </div>

          {/* Avatar */}
          <h3 className="sgv2__h3">Avatar</h3>
          <div className="sgv2__btn-row">
            <Spec name="avatar" onCopy={copy} copied={copied}>
              <span className="sgv2__stage-avatar">GB</span>
            </Spec>
          </div>

          {/* Chips / filter pills */}
          <h3 className="sgv2__h3">Chips / filter pills</h3>
          <div className="sgv2__btn-row">
            <Spec name="chip-default" onCopy={copy} copied={copied}>
              <span className="sgv2__stage-chip">In stock</span>
            </Spec>
            <Spec name="chip-active" onCopy={copy} copied={copied}>
              <span className="sgv2__stage-chip sgv2__stage-chip--active">Consignment <span aria-hidden="true">×</span></span>
            </Spec>
          </div>

          {/* Radio group */}
          <h3 className="sgv2__h3">Radio group</h3>
          <div className="sgv2__btn-row">
            <Spec name="radio-group" onCopy={copy} copied={copied}>
              <div className="sgv2__stage-radios">
                <label className="sgv2__stage-radio"><span className="sgv2__stage-radio-dot sgv2__stage-radio-dot--on" /> Straight purchase</label>
                <label className="sgv2__stage-radio"><span className="sgv2__stage-radio-dot" /> Consignment</label>
              </div>
            </Spec>
          </div>

          {/* Alert */}
          <h3 className="sgv2__h3">Alert / inline notice</h3>
          <div className="sgv2__btn-row">
            <Spec name="alert-info" onCopy={copy} copied={copied}>
              <div className="sgv2__stage-alert sgv2__stage-alert--info">Heads up — this listing has no competitive price yet.</div>
            </Spec>
            <Spec name="alert-error" onCopy={copy} copied={copied}>
              <div className="sgv2__stage-alert sgv2__stage-alert--error">Couldn't save. Check the highlighted fields.</div>
            </Spec>
            <Spec name="alert-success" onCopy={copy} copied={copied}>
              <div className="sgv2__stage-alert sgv2__stage-alert--success">Saved ✓</div>
            </Spec>
          </div>

          {/* Loading spinner */}
          <h3 className="sgv2__h3">Loading spinner</h3>
          <div className="sgv2__btn-row">
            <Spec name="spinner" onCopy={copy} copied={copied}>
              <span className="sgv2__stage-spinner" aria-label="Loading" />
            </Spec>
            <Spec name="spinner-button" onCopy={copy} copied={copied}>
              <button className="sgv2__btn sgv2__btn--primary" disabled><span className="sgv2__stage-spinner sgv2__stage-spinner--sm" /> Saving…</button>
            </Spec>
          </div>

          {/* Progress bar */}
          <h3 className="sgv2__h3">Progress bar</h3>
          <div className="sgv2__btn-row">
            <Spec name="progress" onCopy={copy} copied={copied}>
              <span className="sgv2__stage-progress"><span className="sgv2__stage-progress-fill" style={{ width: "62%" }} /></span>
            </Spec>
          </div>

          {/* Pagination */}
          <h3 className="sgv2__h3">Pagination</h3>
          <div className="sgv2__btn-row">
            <Spec name="pagination" onCopy={copy} copied={copied}>
              <nav className="sgv2__stage-pager">
                <button className="sgv2__stage-page">‹</button>
                <button className="sgv2__stage-page sgv2__stage-page--active">1</button>
                <button className="sgv2__stage-page">2</button>
                <button className="sgv2__stage-page">3</button>
                <button className="sgv2__stage-page">›</button>
              </nav>
            </Spec>
          </div>

          {/* Tooltip */}
          <h3 className="sgv2__h3">Tooltip</h3>
          <div className="sgv2__btn-row">
            <Spec name="tooltip" onCopy={copy} copied={copied}>
              <span className="sgv2__stage-tip">
                <span className="sgv2__stage-tip-bubble">WatchFinder rejected this listing</span>
                <span className="sgv2__stage-tip-anchor">WF</span>
              </span>
            </Spec>
          </div>

          {/* Modal */}
          <h3 className="sgv2__h3">Modal / dialog</h3>
          <div className="sgv2__btn-row">
            {/* Standard confirm — a normal, reversible action keeps the purple
                primary button. */}
            <Spec name="modal" onCopy={copy} copied={copied}>
              <div className="sgv2__stage-modal">
                <p className="sgv2__stage-modal-title">Publish this listing?</p>
                <p className="sgv2__stage-modal-body">It will go live on all connected marketplaces.</p>
                <div className="sgv2__stage-modal-actions">
                  <button className="sgv2__btn sgv2__btn--ghost">Cancel</button>
                  <button className="sgv2__btn sgv2__btn--primary">Publish</button>
                </div>
              </div>
            </Spec>
            {/* Destructive confirm — a PERMANENT delete uses a RED button so the
                irreversible action is unmistakable. */}
            <Spec name="modal-delete" onCopy={copy} copied={copied}>
              <div className="sgv2__stage-modal">
                <p className="sgv2__stage-modal-title">Delete this listing?</p>
                <p className="sgv2__stage-modal-body">This permanently removes it and can't be undone.</p>
                <div className="sgv2__stage-modal-actions">
                  <button className="sgv2__btn sgv2__btn--ghost">Cancel</button>
                  <button className="sgv2__btn sgv2__btn--danger">Delete</button>
                </div>
              </div>
            </Spec>
          </div>

          {/* Money & date inputs */}
          <h3 className="sgv2__h3">Money &amp; date inputs</h3>
          <div className="sgv2__btn-row">
            <Spec name="input-money" onCopy={copy} copied={copied}>
              <span className="sgv2__stage-money"><span className="sgv2__stage-money-prefix">HK$</span><input className="sgv2__search-field" defaultValue="9,000" /></span>
            </Spec>
            <Spec name="input-date" onCopy={copy} copied={copied}>
              {/* Date is ALWAYS day-month-year, left to right (DD-MM-YYYY). */}
              <span className="sgv2__stage-money"><input className="sgv2__search-field" defaultValue="06-08-2026" placeholder="DD-MM-YYYY" /><svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{opacity:.6}}><rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M3 9h18M8 2v4M16 2v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg></span>
            </Spec>
            {/* Working picker — click the calendar icon to open the month grid. */}
            <Spec name="input-date-picker" onCopy={copy} copied={copied}>
              <DatePickerDemo />
            </Spec>
          </div>
        </section>
      </div>

      {/* Toast — confirms a command ran (success micro-interaction). */}
      <AnimatePresence>
        {toast && (
          <motion.div
            className="sgv2__toast"
            role="status"
            aria-live="polite"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ type: "spring", stiffness: 420, damping: 30 }}
          >
            <span className="sgv2__toast-dot" aria-hidden="true" />
            Ran <strong>{toast}</strong>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StyleGuideV2Page;
